// --- Dependencies --------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import type { TyApiResponse, TyCategory } from "@/types";
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import ActionsBar from "@/UI/components/ActionsBar";
import CategoryCard from "@/UI/cards/CategoryCard";
import Skeleton from "@/UI/atoms/Skeleton";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import FormCategoryPopup from "@/UI/popups/FormCategoryPopup";
import MoreActionsPopup from "@/UI/popups/MoreActionsPopup";
import SortCategoriesPopup from "@/UI/popups/SortCategoriesPopup";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

export default function MenuScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const { addComp, removeComp } = usePopup();
    const { canEdit } = usePermission();

    const { data, loading, error, invoke } = useFetch<TyApiResponse<{ categories: TyCategory[] }>>("/menu/categories");

    const categories = data?.data?.categories ?? [];

    // --- Delete ----------------------------------------------------------------------------------

    const [deletingId, setDeletingId] = useState<string | null>(null);

    const { data: deleteData, invoke: invokeDelete } = useFetch<TyApiResponse<Record<string, never>>>(
        `/menu/categories/${deletingId ?? "x"}`,
        { method: "DELETE" },
        false
    );

    useEffect(() => {
        if (!deletingId) return;
        invokeDelete();
    }, [deletingId]);

    useEffect(() => {
        if (!deleteData?.success) return;
        setDeletingId(null);
        invoke();
    }, [deleteData]);

    // --- Handlers --------------------------------------------------------------------------------

    const handleSort = () => {
        if (categories.length < 2) return;
        addComp({
            name: "sort-categories",
            Component: SortCategoriesPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: { categories, onSuccess: invoke },
        });
    };

    const handleAdd = () => {
        addComp({
            name: "form-category",
            Component: FormCategoryPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: { onSuccess: invoke },
        });
    };

    const handleEdit = (category: TyCategory) => {
        addComp({
            name: "form-category",
            Component: FormCategoryPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: { category, onSuccess: invoke },
        });
    };

    const handleDeletePrompt = (category: TyCategory) => {
        removeComp("confirm-delete-category");
        addComp({
            name: "confirm-delete-category",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                message: t.formCategory.deleteMessage,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.formCategory.deleteConfirm,
                onConfirm: () => setDeletingId(category._id),
            },
        });
    };

    const handleMore = (category: TyCategory, e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const name = `more-actions-${category._id}`;
        removeComp(name);
        addComp({
            name,
            Component: MoreActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            coverClose: true,
            anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
            details: {
                actions: [
                    { label: t.formCategory.editAction, icon: "/icons/edit-icon.svg", handler: () => handleEdit(category) },
                    { label: t.formCategory.deleteAction, icon: "/icons/delete-icon.svg", handler: () => handleDeletePrompt(category), danger: true },
                ],
            },
        });
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <div className="menu-screen">
            <ActionsBar
                title={t.menu.categories}
                addLabel={t.menu.addLabel}
                onAdd={canEdit("menu") ? handleAdd : undefined}
                onSort={canEdit("menu") && categories.length > 1 ? handleSort : undefined}
            />
            <div className="menu-screen-content">
                {loading && (
                    <div className="menu-screen-grid">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton key={i} className="menu-screen-skeleton" />
                        ))}
                    </div>
                )}
                {error && <p className="menu-screen-message menu-screen-message--error">{error}</p>}
                {!loading && !error && categories.length === 0 && (
                    <div className="menu-screen-empty">
                        <div className="menu-screen-empty-icon">
                            <img src="/icons/category-icon.svg" alt="" width={32} height={32} />
                        </div>
                        <div className="menu-screen-empty-text">
                            <p className="menu-screen-empty-title">{t.menu.emptyCategoriesTitle}</p>
                            <p className="menu-screen-empty-subtitle">{t.menu.emptyCategoriesSubtitle}</p>
                        </div>
                    </div>
                )}
                {!loading && !error && categories.length > 0 && (
                    <div className="menu-screen-grid">
                        {categories.map((category) => (
                            <CategoryCard
                                key={category._id}
                                category={category}
                                onPress={() => navigate(`/menu/categories/${category._id}`, { state: { name: category.name } })}
                                onMore={canEdit("menu") ? (e) => handleMore(category, e) : undefined}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
